import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const STORAGE_KEY = "color-mode";

const ColorModeContext = createContext({ mode: "light", toggle: () => {} });
export const useColorMode = () => useContext(ColorModeContext);

export default function ColorModeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "light";
    } catch {
      return "light";
    }
  });

  // Reflect the mode on the document so CSS variables ([data-theme]) flip too.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    document.body.setAttribute("data-theme", mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  const value = useMemo(
    () => ({
      mode,
      toggle: () => setMode((m) => (m === "light" ? "dark" : "light")),
      setMode,
    }),
    [mode],
  );

  const theme = useMemo(
    () =>
      createTheme({
        typography: { fontFamily: `"Figtree"` },
        palette: {
          mode,
          primary: { main: "#5751E9" },
          ...(mode === "dark"
            ? {
                background: { default: "#121212", paper: "#1E1E1E" },
                text: { primary: "#EDEDED", secondary: "#A0A0A0" },
                divider: "#383838",
              }
            : {
                background: { default: "#F6F7F9", paper: "#FFFFFF" },
                text: { primary: "#22272B", secondary: "#6B7280" },
              }),
        },
        components: {
          // Project-wide switch color
          MuiSwitch: {
            styleOverrides: {
              switchBase: {
                "&.Mui-checked": {
                  color: "#FFA72B",
                  "&:hover": {
                    backgroundColor: "rgba(255, 167, 43, 0.08)",
                  },
                },
                "&.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#FFA72B",
                },
              },
            },
          },
        },
      }),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
